import { type ComponentProps, type PartData } from '@enonic/react-components'
import ContentDownloadFormPrototype from '/react4xp/components/prototype/ContentDownloadForm'
import type { ContentDownloadFormData, ContentDownloadFormLabels } from '/react4xp/components/prototype/ContentDownloadForm/types'

/**
 * Adapter for the styled prototype Content Download Form: the markup and styling
 * all come from the prototype component, this View supplies the Enonic data and
 * posts to the content-download-form service.
 */
type ContentDownloadFormPartData = {
	submitUrl?: string
	contentId?: string
	componentPath?: string
	headline?: string
	intro?: string
	formTitle?: string
	formIntro?: string
	// `resolveLink` returns either undefined or a complete link.
	privacyLink?: { label: string; url: string; isExternal: boolean }
	tooltipLabel?: string
	motivationOptions?: { label?: string; value?: string }[]
	hideResetButton?: boolean
	secondaryButton?: { label: string; link: { url: string; isExternal: boolean } }
	labels?: ContentDownloadFormLabels
}

export const ContentDownloadForm = ({ data }: ComponentProps<PartData>) => {
	const {
		submitUrl,
		contentId,
		componentPath,
		headline,
		intro,
		formTitle,
		formIntro,
		privacyLink,
		tooltipLabel,
		motivationOptions,
		hideResetButton,
		secondaryButton,
		labels,
	} = (data || {}) as ContentDownloadFormPartData

	const handleSubmit = async (values: ContentDownloadFormData) => {
		// Sent as urlencoded rather than multipart so the service can read the
		// fields straight off `request.params`.
		const body = new URLSearchParams()
		body.set('name', values.name)
		body.set('email', values.email)
		body.set('company', values.company)
		body.set('jobRole', values.jobRole)
		body.set('motivation', values.motivation || '')
		body.set('consent', String(values.consent))
		if (contentId) body.set('contentId', contentId)
		if (componentPath) body.set('componentPath', componentPath)
		body.set('pageUrl', window.location.href)

		// The prototype form treats a throw as its error path, so failures must
		// not be returned as a status.
		const response = await fetch(submitUrl!, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
			body: body.toString(),
		})
		if (!response.ok) {
			throw new Error(`Content Download Form submission failed: ${response.status}`)
		}
	}

	return (
		<ContentDownloadFormPrototype
			headline={headline}
			intro={intro}
			formTitle={formTitle}
			formIntro={formIntro}
			privacyLink={privacyLink && { url: privacyLink.url, isExternal: privacyLink.isExternal }}
			tooltipLabel={tooltipLabel}
			motivationOptions={motivationOptions}
			hideResetButton={hideResetButton}
			secondaryButton={secondaryButton}
			labels={labels}
			onSubmit={handleSubmit}
		/>
	)
}
